import { useSimilarAssets, useUpdateAssetsMutation } from "@/hooks/assets";
import { useToast } from "@/hooks/toast";
import { useUploadUpdateMutation } from "@/hooks/uploads";
import { useAdminUsersQuery } from "@/hooks/user";
import Checkbox from "@ingka/checkbox";
import ListView, { ListViewItem } from "@ingka/list-view";
import SSRIcon from "@ingka/ssr-icon";
import speechBubbleActive from "@ingka/ssr-icon/paths/speech-bubble-active";
import {
  UpdateAssetDtoStateEnum,
  UpdateUploadDtoStateEnum,
  UploadWithAssetsDto,
} from "generated";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { CommentPane } from "../Comment/CommentPane";
import { AdminAssetListView } from "./AdminAssetListView";
import { AdminAssetNames } from "./AdminAssetNames";
import { AdminAssetsOverview } from "./AdminAssetsOverview";
import {
  AUTO_SELECTED_STATES,
  metadataSections,
  SELECT_ALL,
} from "./constants";
import DisplayApproveErrors from "./DisplayApproveErrors";
import { CachedFileData, ModalState } from "./types";
import AdminDropdown from "./AdminDropDown";
import BottomButtons from "./BottomButtons";
import TopButtons from "./TopButton";
import MetadataTitle from "./MetadataTitle";
import UploadTitle from "../UploadTitle/UploadTitle";
import { blobUrlToFile, genericOnChange, getAriaLabel } from "./utils";
import { SimilarAssets } from "./SimilarAssets";
import { useQueryClient, useIsFetching } from "@tanstack/react-query";
import GenericModal from "../GenericModal/GenericModal";
import {
  AssetsChangeMap,
  AssetValidationEntry,
  isAssetValidationEntry,
} from "@/services/store/slices/mandatoryMetaData/types";
import { isUploadFinalized } from "@/components/MetadataFields/formFieldsAccessRules";
import { useStore } from "@/services/store";
import { sendErrorToSentry } from "../../sentry";

export const AdminView = (upload: UploadWithAssetsDto) => {
  const assets = upload.assets;
  const error = useStore((state) => state.error);
  const updateAssetsMutation = useUpdateAssetsMutation();
  const uploadUpdateMutation = useUploadUpdateMutation();
  const adminsRequest = useAdminUsersQuery();
  const navigate = useNavigate();
  const initialAssetSelection = assets
    .filter((asset) => AUTO_SELECTED_STATES.includes(asset.state))
    .map((asset) => asset.id.toString());
  const [assetSelection, setAssetSelection] = useState<string[]>([
    ...(initialAssetSelection.length === assets.length
      ? [SELECT_ALL, ...initialAssetSelection]
      : []),
  ]);
  const [visibility, setVisibility] = useState<string[]>([
    ...metadataSections
      .filter((section) => section.isOptionalField)
      .map((section) => section.id),
  ]);
  const [uploadStateChangePending, setUploadStateChangePending] =
    useState(false);

  const [modalState, setModalstate] = useState(ModalState.Hidden);
  const [showCommentIcon, setShowCommentIcon] = useState(upload.newComments);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [selectedAssetId, setSelectedAssetId] = useState<number>();
  const { similarAssets, checkForSimilarAssets, loadingSimilarAssets } =
    useSimilarAssets();
  const selectedSimilarityResult =
    selectedAssetId !== undefined ? similarAssets[selectedAssetId] : undefined;
  const isFetchingAssetsFile =
    useIsFetching({ queryKey: ["assets", "file"] }) > 0;
  const { state, type } = upload;
  const uploadDone =
    state === "PARTIAL_APPROVED" || state === "DENIED" || state === "DONE";

  const errors = useMemo(
    () => Object.values(error || {}).flatMap((e) => e),
    [error],
  );

  const updateUploadState = useCallback(
    async (newState: UpdateUploadDtoStateEnum) => {
      await uploadUpdateMutation.mutateAsync({
        id: upload.id,
        updateUploadDto: {
          state: newState,
        },
      });
    },
    [upload.id, uploadUpdateMutation],
  );

  useEffect(() => {
    if (errors.length > 0) {
      toast(<DisplayApproveErrors errors={errors} />);
    }
  }, [toast, errors]);

  useEffect(() => {
    const cachedAssetFiles = queryClient.getQueryCache().findAll({
      queryKey: ["assets", "file"],
    });

    if (cachedAssetFiles.length === 0) {
      return;
    }

    const resolvedFilePromises = cachedAssetFiles
      .map((fileData) => {
        const data = fileData.state.data as CachedFileData;
        if (!data) {
          return null;
        }

        return blobUrlToFile(
          data.blobUrl,
          data.id.toString(),
          data.data.type,
        ).catch((error) => {
          console.error("Error creating file for blob url", data.blobUrl);
          sendErrorToSentry(error, {
            extra: { blobUrl: data.blobUrl },
          });
          return null;
        });
      })
      .filter((item) => !!item);

    if (upload.type === "IMAGE") {
      Promise.all(resolvedFilePromises).then((files) => {
        const resolvedFiles = files.filter((file): file is File => !!file);
        if (resolvedFiles.length > 0) {
          checkForSimilarAssets(resolvedFiles);
        }
      });
    }
  }, [queryClient, isFetchingAssetsFile, upload.type, checkForSimilarAssets]);

  useEffect(() => {
    if (uploadDone || uploadUpdateMutation.isPending) {
      return;
    }
    const result = assets.reduce(
      (acc, { state }) => {
        acc.allDone = acc.allDone && state === "DONE";
        acc.allDenied = acc.allDenied && state === "DENIED";
        acc.partialApproved =
          acc.partialApproved && (state === "DONE" || state === "DENIED");
        return acc;
      },
      {
        allDone: true,
        allDenied: true,
        partialApproved: true,
      } as Record<string, boolean>,
    );

    let newState: "DONE" | "DENIED" | "PARTIAL_APPROVED" | null = null;

    if (result.allDone) {
      newState = "DONE";
    } else if (result.allDenied) {
      newState = "DENIED";
    } else if (result.partialApproved) {
      newState = "PARTIAL_APPROVED";
    }

    if (newState) {
      setUploadStateChangePending(true);
      updateUploadState(newState);
    }
  }, [assets, uploadDone, uploadUpdateMutation.isPending, updateUploadState]);

  useEffect(() => {
    if (uploadUpdateMutation.isSuccess && uploadStateChangePending) {
      setUploadStateChangePending(false);
      navigate("/overview");
    }
  }, [uploadUpdateMutation.isSuccess, uploadStateChangePending, navigate]);

  useEffect(() => {
    if (uploadUpdateMutation.isError && uploadStateChangePending) {
      setUploadStateChangePending(false);
      toast("Failed to update upload status");
    }
  }, [uploadUpdateMutation.isError, uploadStateChangePending, toast]);

  const handleAssetSelection = (assetId: string) => {
    genericOnChange(assetId, assets, assetSelection, setAssetSelection);
  };

  const handleVisibilityChange = (metadataField: string) => {
    genericOnChange(metadataField, metadataSections, visibility, setVisibility);
  };

  const renderAssetStatus = (
    assets: string[],
    newState: UpdateAssetDtoStateEnum,
  ) => {
    const status = {
      DONE: "APPROVED",
      DENIED: "DENIED",
    } as Record<UpdateAssetDtoStateEnum, string>;
    return (
      <>
        <div>{status[newState]}</div>
        {assets.map((asset) => (
          <div key={asset}>{asset}</div>
        ))}
      </>
    );
  };

  const updateAssetStates = async (newState: UpdateAssetDtoStateEnum) => {
    const selectedAssets = assetSelection
      .map(
        (selectedId) => assets.find((asset) => asset.id === +selectedId)?.name,
      )
      .filter((name) => name !== undefined);

    try {
      await updateAssetsMutation.mutateAsync(
        assetSelection.reduce((acc, id) => {
          if (id !== SELECT_ALL) {
            acc[id] = { state: newState };
          }
          return acc;
        }, {} as AssetsChangeMap),
      );

      if (
        !updateAssetsMutation.isPending &&
        Boolean(errors.length) &&
        (newState === "DONE" || newState === "DENIED")
      ) {
        toast(renderAssetStatus(selectedAssets, newState));
      }
    } catch {
      toast(`Failed to update assets: ${selectedAssets}`);
    }
  };

  const validationErrors: AssetValidationEntry[] = errors
    .flatMap((e) => e.validationErrors ?? [])
    .filter(isAssetValidationEntry);

  return (
    <div className="mb-space150">
      <UploadTitle upload={upload} adminPage>
        {adminsRequest.isSuccess && (
          <AdminDropdown
            assignee={upload.assignee}
            admins={adminsRequest.data}
            uploadState={state}
            onSelect={async (assigneeId) => {
              await uploadUpdateMutation.mutateAsync({
                id: upload.id,
                updateUploadDto: {
                  assigneeId,
                },
              });
            }}
          />
        )}
      </UploadTitle>
      <div className="mb-space150 flex justify-between">
        <MetadataTitle
          assetCount={assets.length}
          handleChangeVisible={handleVisibilityChange}
          visibility={visibility}
        />
        <TopButtons
          onApprove={() => updateAssetStates("DONE")}
          onDeny={() => updateAssetStates("DENIED")}
          onFeedback={() => updateUploadState("ADMIN_FEEDBACK")}
          assetSelection={assetSelection}
          uploadDone={uploadDone}
          isLoading={updateAssetsMutation.isPending}
        />
      </div>
      <div className="mb-space200 grid grid-cols-3">
        <AdminAssetsOverview
          assets={assets}
          visibility={visibility}
          handleChangeVisible={handleVisibilityChange}
        />
        <div>
          <ListView id={"list-items"} size="large">
            <ListViewItem
              inset
              emphasised
              onClick={() => setModalstate(ModalState.Comments)}
              control="navigational"
              controlIcon="arrowRight"
              title="View comments"
              quantityLabel={
                showCommentIcon ? <SSRIcon paths={speechBubbleActive} /> : ""
              }
            />
            <ListViewItem
              inset
              emphasised
              onClick={() => setModalstate(ModalState.AssetNames)}
              control="navigational"
              controlIcon="arrowRight"
              title="View edited asset name(s)"
            />
            {type === "VIDEO" && (
              <ListViewItem
                inset
                emphasised
                onClick={() =>
                  navigate(`/admin/${upload.id}/mandatory-video-metadata`)
                }
                control="navigational"
                controlIcon="arrowRight"
                title="View accessibility data"
              />
            )}
          </ListView>
          <GenericModal
            modalVisible={modalState !== ModalState.Hidden}
            hideModal={() => {
              if (modalState === ModalState.Comments) {
                setShowCommentIcon(false);
              }
              setModalstate(ModalState.Hidden);
            }}
            ariaLabel={getAriaLabel(modalState)}
          >
            {modalState === ModalState.Comments && (
              <CommentPane scrollHeight="100%" upload={upload} />
            )}
            {modalState === ModalState.AssetNames && (
              <AdminAssetNames assets={assets} />
            )}
            {modalState === ModalState.SimilarAssets && selectedAssetId && (
              <SimilarAssets
                similarAssets={selectedSimilarityResult?.result || []}
                status={selectedSimilarityResult?.status}
                errorMessage={selectedSimilarityResult?.message}
                responseReceived={selectedSimilarityResult !== undefined}
              />
            )}
          </GenericModal>
        </div>
      </div>
      <div className="flex items-end justify-between">
        <Checkbox
          id={SELECT_ALL}
          value=""
          checked={assetSelection.includes(SELECT_ALL)}
          label="Select all"
          disabled={isUploadFinalized({ uploadState: state })}
          onChange={() => handleAssetSelection(SELECT_ALL)}
        />
        <BottomButtons
          onOk={() => updateAssetStates("VIEWED")}
          onEdit={() => {
            const itemPath = assetSelection
              .filter((v) => v !== SELECT_ALL)
              .toString();
            const editPath =
              type === "DOCUMENT"
                ? `/admin/${upload.id}/edit/mandatory-document-metadata/${itemPath}`
                : `/admin/${upload.id}/edit/mandatory-metadata/${itemPath}`;
            navigate(editPath);
          }}
          assetSelection={assetSelection}
          uploadDone={uploadDone}
        />
      </div>
      <AdminAssetListView
        assets={assets}
        isChecked={assetSelection}
        visibility={visibility}
        errors={validationErrors}
        handleChangeChecked={handleAssetSelection}
        loadingSimilarAssets={loadingSimilarAssets}
        uploadState={state}
        onSimilarAssets={(assetId) => {
          setSelectedAssetId(assetId);
          setModalstate(ModalState.SimilarAssets);
        }}
      />
    </div>
  );
};

export default AdminView;
